// Instellingen: tags hernoemen, de knop voor je bladwijzerbalk, tokens beheren, en je notities
// in- en uitpakken.
//
// Dingen die je zelden doet maar wel ergens moeten kunnen. Een token is voor alles wat
// niet door de browser komt — een Shortcut op je telefoon, een scriptje. Je ziet hem één
// keer; daarna staat er alleen nog een hash op de server.

import { useEffect, useState } from "react";
import { api, type ImportUitslag, type KluisStand, type TagRegel, type TokenRegel } from "../api.ts";
import { Icoon } from "./Icoon.tsx";
import { maakBookmarklet } from "./bookmarklet.ts";
import { Herinneringen } from "./Herinneringen.tsx";
import { KluisInstellen } from "./Kluis.tsx";

export function Instellingen({ terug }: { terug: () => void }) {
  const [tags, setTags] = useState<TagRegel[]>([]);
  const [tokens, setTokens] = useState<TokenRegel[]>([]);
  const [nieuwToken, setNieuwToken] = useState<string | null>(null);
  // De knop hangt aan het adres waar de app draait, meer heeft hij niet nodig.
  const clipperKnop = maakBookmarklet(location.origin);
  const [tokenNaam, setTokenNaam] = useState("");
  const [hernoemen, setHernoemen] = useState<{ oud: string; nieuw: string } | null>(null);
  const [melding, setMelding] = useState<string | null>(null);
  const [uitslag, setUitslag] = useState<ImportUitslag | null>(null);
  const [bezig, setBezig] = useState(false);
  const [kluis, setKluis] = useState<KluisStand | null>(null);

  /** Een zip kiezen en naar de server sturen. Alles blijft staan tenzij je uitdrukkelijk kiest
      voor overschrijven — een import hoort nooit per ongeluk iets te wissen. */
  function kiesImport(overschrijven: boolean) {
    const kiezer = document.createElement("input");
    kiezer.type = "file";
    kiezer.accept = ".zip,application/zip";
    kiezer.onchange = async () => {
      const bestand = kiezer.files?.[0];
      if (!bestand) return;
      setBezig(true);
      setUitslag(null);
      try {
        const uit = await api.importeer(bestand, overschrijven);
        setUitslag(uit);
        setMelding(`${uit.nieuw} notities toegevoegd`);
      } catch (fout) {
        setMelding(`Inlezen is niet gelukt: ${(fout as Error).message}`);
      } finally {
        setBezig(false);
      }
    };
    kiezer.click();
  }

  useEffect(() => {
    void api.tags().then((u) => setTags(u.tags)).catch(() => undefined);
    void api.tokens().then((u) => setTokens(u.tokens)).catch(() => undefined);
    void api.kluis().then(setKluis).catch(() => undefined);
  }, []);

  return (
    <div style={{ maxWidth: 720, margin: "0 auto", padding: "20px 18px 60px", display: "flex", flexDirection: "column", gap: 26 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <button className="knop stil" onClick={terug} aria-label="Terug">
          <Icoon naam="terug" grootte={18} />
        </button>
        <h2 style={{ fontFamily: "var(--schrift-titel)", fontSize: 24, margin: 0 }}>Instellingen</h2>
      </div>

      <section style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <h3 style={{ fontSize: 15, margin: 0 }}>Tags</h3>
        <p style={{ margin: 0, fontSize: 13, color: "var(--gedempt)" }}>
          Tags staan in de tekst van je notities. Hernoemen past de tekst overal aan, ook in de
          geneste tags eronder.
        </p>
        {tags.length === 0 && <p className="leeg">Nog geen tags. Typ er een in een notitie: #klant</p>}
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {tags.map((tag) => (
            <div key={tag.naam} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 8px", borderRadius: 8 }}>
              <Icoon naam="tag" grootte={15} />
              {hernoemen?.oud === tag.naam ? (
                <>
                  <input
                    autoFocus
                    value={hernoemen.nieuw}
                    onChange={(e) => setHernoemen({ oud: tag.naam, nieuw: e.target.value })}
                    onKeyDown={async (e) => {
                      if (e.key === "Escape") setHernoemen(null);
                      if (e.key === "Enter" && hernoemen.nieuw.trim()) {
                        const uit = await api.hernoemTag(tag.naam, hernoemen.nieuw.trim());
                        setTags(uit.tags);
                        setHernoemen(null);
                        setMelding(`${uit.geraakt} notitie${uit.geraakt === 1 ? "" : "s"} aangepast`);
                      }
                    }}
                    style={{ flexGrow: 1, padding: "6px 10px", border: "1px solid var(--lijn)", borderRadius: 8 }}
                  />
                  <button className="knop stil" onClick={() => setHernoemen(null)}>Annuleren</button>
                </>
              ) : (
                <>
                  <span style={{ flexGrow: 1 }}>#{tag.naam}</span>
                  <span style={{ fontSize: 12, color: "var(--gedempt)" }}>{tag.aantal}</span>
                  <button className="knop stil" onClick={() => setHernoemen({ oud: tag.naam, nieuw: tag.naam })}>
                    Hernoemen
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
      </section>

      <KluisInstellen stand={kluis} opStand={setKluis} opMelding={setMelding} />

      <Herinneringen opMelding={setMelding} />

      <section style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <h3 style={{ fontSize: 15, margin: 0 }}>Pagina's bewaren</h3>
        <p style={{ margin: 0, fontSize: 13, color: "var(--gedempt)" }}>
          Een artikel dat je wilt houden gaat als gewone notitie in je Inbox: de tekst zonder
          menu's en cookiemeldingen, de afbeeldingen als bijlage, en onderaan waar het vandaan
          kwam. Op de telefoon deel je de pagina met Notities. Op de laptop hangt deze knop in
          je bladwijzerbalk.
        </p>

        <div style={{ padding: 12, border: "1px solid var(--accent-rand)", background: "var(--accent-zacht)", borderRadius: 10, display: "flex", flexDirection: "column", gap: 10 }}>
          <strong style={{ fontSize: 13 }}>Sleep deze knop naar je bladwijzerbalk:</strong>
          <div>
            {/* React zet een javascript:-adres niet in `href`, dus doen we het er zelf in.
                Zonder echt adres valt er niets te slepen en is de knop waardeloos. */}
            <a
              ref={(element) => {
                element?.setAttribute("href", clipperKnop);
              }}
              className="knop hoofd"
              draggable
              onClick={(e) => e.preventDefault()}
              style={{ textDecoration: "none", cursor: "grab" }}
            >
              <Icoon naam="wolk" grootte={15} /> Bewaar in Notities
            </a>
          </div>
          <p style={{ margin: 0, fontSize: 12, color: "var(--gedempt)" }}>
            Balk niet zichtbaar? Ctrl+Shift+B. Er staat geen wachtwoord in de knop: hij opent een
            klein venster van deze app, en dáár wordt de pagina bewaard — met de aanmelding die je
            toch al hebt.
          </p>
          <div>
            <button className="knop" onClick={() => { void navigator.clipboard?.writeText(clipperKnop); setMelding("Gekopieerd"); }}>
              Kopiëren
            </button>
          </div>
        </div>
      </section>

      <section style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <h3 style={{ fontSize: 15, margin: 0 }}>Tokens</h3>
        <p style={{ margin: 0, fontSize: 13, color: "var(--gedempt)" }}>
          Voor alles wat buiten de browser om iets wil vastleggen. Stuur een POST naar{" "}
          <code style={{ fontFamily: "var(--schrift-code)" }}>/api/ext/vastleggen</code> met de kop{" "}
          <code style={{ fontFamily: "var(--schrift-code)" }}>Authorization: Bearer …</code> en een JSON-lichaam met
          titel, tekst of url. Het beland in je Inbox. Naar{" "}
          <code style={{ fontFamily: "var(--schrift-code)" }}>/api/ext/clip</code> mag ook, met een{" "}
          <code style={{ fontFamily: "var(--schrift-code)" }}>url</code> erin: dan haalt de NAS de hele
          pagina op en bewaart hem als notitie.
        </p>

        {nieuwToken && (
          <div style={{ padding: 12, border: "1px solid var(--accent-rand)", background: "var(--accent-zacht)", borderRadius: 10, display: "flex", flexDirection: "column", gap: 6 }}>
            <strong style={{ fontSize: 13 }}>Kopieer dit nu — je ziet het hierna nooit meer.</strong>
            <code style={{ fontFamily: "var(--schrift-code)", fontSize: 13, wordBreak: "break-all" }}>{nieuwToken}</code>
            <div>
              <button className="knop" onClick={() => { void navigator.clipboard?.writeText(nieuwToken); setMelding("Gekopieerd"); }}>
                Kopiëren
              </button>{" "}
              <button className="knop stil" onClick={() => setNieuwToken(null)}>Verbergen</button>
            </div>
          </div>
        )}

        <div style={{ display: "flex", gap: 8 }}>
          <input
            value={tokenNaam}
            onChange={(e) => setTokenNaam(e.target.value)}
            placeholder="Waarvoor is dit token? (bijv. Telefoon)"
            style={{ flexGrow: 1, padding: "8px 12px", border: "1px solid var(--lijn)", borderRadius: 8 }}
          />
          <button
            className="knop hoofd"
            onClick={async () => {
              const gemaakt = await api.maakToken(tokenNaam || "Naamloos token");
              setNieuwToken(gemaakt.token);
              setTokenNaam("");
              setTokens((huidig) => [gemaakt.regel, ...huidig]);
            }}
          >
            <Icoon naam="sleutel" grootte={15} /> Nieuw token
          </button>
        </div>

        {tokens.length === 0 && <p className="leeg">Nog geen tokens.</p>}
        {tokens.map((token) => (
          <div key={token.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 4px", borderTop: "1px solid var(--lijn)" }}>
            <Icoon naam="sleutel" grootte={15} />
            <span style={{ flexGrow: 1 }}>{token.naam}</span>
            <span style={{ fontSize: 12, color: "var(--gedempt)" }}>
              {token.laatstGebruikt ? `laatst gebruikt ${new Date(token.laatstGebruikt).toLocaleDateString("nl-NL")}` : "nog niet gebruikt"}
            </span>
            <button
              className="knop stil"
              onClick={async () => {
                const uit = await api.trekTokenIn(token.id);
                setTokens(uit.tokens);
                setMelding("Token ingetrokken");
              }}
            >
              Intrekken
            </button>
          </div>
        ))}
      </section>

      <section style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <h3 style={{ fontSize: 15, margin: 0 }}>Je notities meenemen</h3>
        <p style={{ margin: 0, fontSize: 13, color: "var(--gedempt)" }}>
          De export is een zip met gewone markdown-bestanden: één per notitie, een map per
          notitieboek, en bovenin elk bestand het id dat nooit verandert. Elke nacht maakt de
          app er zelf ook een op de NAS. Diezelfde zip kun je hier weer inlezen — dat is meteen
          de proef of de export echt compleet is.
        </p>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <a className="knop" href="/api/export" download>
            <Icoon naam="download" grootte={15} />
            Alles exporteren
          </a>
          <button className="knop" disabled={bezig} onClick={() => kiesImport(false)}>
            <Icoon naam="inbox" grootte={15} />
            {bezig ? "Bezig…" : "Export inlezen"}
          </button>
          <button
            className="knop"
            disabled={bezig}
            title="Notities die hier al staan worden overschreven met wat er in het bestand staat"
            onClick={() => kiesImport(true)}
          >
            Inlezen én overschrijven
          </button>
        </div>
        {uitslag && (
          <div style={{ fontSize: 13, background: "var(--lijstvlak)", border: "1px solid var(--lijn)", borderRadius: 10, padding: "10px 12px" }}>
            <strong>{uitslag.bestanden} bestanden gelezen.</strong> {uitslag.nieuw} nieuw, {uitslag.bijgewerkt} bijgewerkt,{" "}
            {uitslag.overgeslagen} overgeslagen omdat ze er al waren
            {uitslag.boeken > 0 ? `, ${uitslag.boeken} notitieboek${uitslag.boeken === 1 ? "" : "en"} aangemaakt` : ""}.
            {uitslag.meldingen.map((regel) => (
              <div key={regel} style={{ color: "var(--gedempt)", marginTop: 4 }}>
                {regel}
              </div>
            ))}
          </div>
        )}
      </section>

      {melding && (
        <div className="melding" onClick={() => setMelding(null)}>
          {melding}
        </div>
      )}
    </div>
  );
}
